import { useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { DailyMissionProgressBarV2 } from '../../../../components/DailyMissions/components/DailyMissionProgressBarV2/DailyMissionProgressBarV2';
import { MissionsLobbyProgressBar } from '../../../../components/Missions/components/MissionsLobbyProgressBar';
import { MOBILE_MEDIA_QUERY } from '../../../../config/constants';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useFlags } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import { useWidgets, useWidgetsScroll, useWidgetsShadows, WidgetComponent } from '../../../widgets';
import { WIDGETS_GAP_DESKTOP, WIDGETS_GAP_MOBILE } from '../../../widgets/constants';

import styles from './Widgets.module.scss';

type DailyMissionDisplayType = 'inline' | 'new-line' | 'none';

const getDailyMissionDisplayType = ({
  progressBarExists,
  widgetsCount,
  isMobile,
}: {
  progressBarExists: boolean;
  widgetsCount: number;
  isMobile: boolean;
}): DailyMissionDisplayType => {
  if (!progressBarExists) {
    return 'none';
  }
  if (widgetsCount === 0) {
    return 'inline';
  }
  if (widgetsCount <= 1 && !isMobile) {
    return 'inline';
  }
  if (widgetsCount > 1 || isMobile) {
    return 'new-line';
  }
  return 'none';
};

function WidgetsComponent() {
  const { missions_v2_enabled } = useFlags();
  const { dailyMissionsStore, missionsStore } = useStores();
  const { containerRef, widgets, allWidgetsVisible } = useWidgets();
  const { showLeftShadow, showRightShadow } = useWidgetsShadows({ containerRef });
  const { scrollWidgets } = useWidgetsScroll({ containerRef });
  const { widgetsStore } = useStores();
  const { currentDailyMission } = dailyMissionsStore;
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const gap = isMobile ? WIDGETS_GAP_MOBILE : WIDGETS_GAP_DESKTOP;

  useEffect(() => {
    if (!widgetsStore.widgetsRef?.current) {
      widgetsStore.widgetsRef.current = {
        scrollWidgets,
      };
    }
  }, [scrollWidgets, widgetsStore.widgetsRef]);

  useEffect(() => {
    widgetsStore.setAllWidgetsVisible(allWidgetsVisible);
  }, [allWidgetsVisible, widgetsStore]);

  const dmProgressBar = missions_v2_enabled ? <MissionsLobbyProgressBar /> : <DailyMissionProgressBarV2 />;

  const progressBarExists = missions_v2_enabled
    ? !!missionsStore.activeMission
    : !!currentDailyMission && ['ACTIVE', 'COMPLETED'].includes(currentDailyMission.status);

  const dailyMissionDisplayType = getDailyMissionDisplayType({
    progressBarExists,
    widgetsCount: widgets.length,
    isMobile,
  });

  return (
    <div className={styles.container}>
      <div ref={containerRef} className={styles.content} style={{ gap }}>
        {widgets.map((widget) => (
          <WidgetComponent key={widget.id} {...widget} />
        ))}
        {dailyMissionDisplayType === 'inline' && <div className={styles.dailyMissionWidget}>{dmProgressBar}</div>}
      </div>
      {showRightShadow && <div className={classNames(styles.gradientShadow, styles.gradientShadowRight)} />}
      {showLeftShadow && <div className={classNames(styles.gradientShadow, styles.gradientShadowLeft)} />}
      {dailyMissionDisplayType === 'new-line' && (
        <div className={styles.dailyMissionWidgetWrapper}>{dmProgressBar}</div>
      )}
    </div>
  );
}

export const Widgets = observer(WidgetsComponent);
